import { BreadcrumbPage } from "@/components/ui/breadcrumb";

import { BreadcrumbItem } from "@/components/ui/breadcrumb";

import { SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";

import { AppSidebar } from "@/components/app-sidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { BreadcrumbList } from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Navigate, Outlet } from "react-router";
import { Toaster } from "@/components/ui/sonner";
import { useParams } from "react-router";
import { Tenant, useSuspenseTenant } from "@/api/tenant.api";
import { useSuspenseUserAccess } from "@/api/user.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { useEffect, useState } from "react";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { NoRoleAssigned } from "./pages/no-role-assigned/no-role-assigned";
import { CommandPalette } from "@/components/command-palette";
import { ThemeToggle } from "@/components/theme-toggle";
import { useWebSocket } from "@/hooks/useWebSocket";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Routes } from "@/router/routes";
import { Initialize } from "@/pages/initialize/initialize";

/**
 * Small live/offline indicator reflecting the realtime websocket connection.
 */
const LiveIndicator = () => {
    const { t } = useTranslation();
    const { getSocket } = useWebSocket();
    const [connected, setConnected] = useState(false);

    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;
        const onConnect = () => setConnected(true);
        const onDisconnect = () => setConnected(false);
        socket.on("connect", onConnect);
        socket.on("disconnect", onDisconnect);
        setConnected(socket.connected);
        return () => {
            socket.off("connect", onConnect);
            socket.off("disconnect", onDisconnect);
        };
    }, [getSocket]);

    return (
        <div className="flex items-center gap-1.5 px-1.5 font-mono text-[11px] text-muted-foreground">
            <span
                className={cn(
                    "size-1.5 rounded-full",
                    connected
                        ? "animate-pulse bg-success shadow-[0_0_0_3px_hsl(152_60%_38%/0.18)]"
                        : "bg-muted-foreground/50",
                )}
            />
            {connected ? t("topbar.live") : t("topbar.offline")}
        </div>
    );
};

/**
 * Resolve tenant access before mounting tenant-scoped content.
 */
export const TenantManagement = () => {
    const { id } = useParams();
    const { data: tenant } = useSuspenseTenant(Number(id));
    const selectedTenantId = useTenantStore((state) => state.selectedTenant?.id);

    useEffect(() => {
        useTenantStore.getState().selectTenant(tenant ?? undefined);
    }, [tenant]);

    if (!tenant) {
        return <Navigate to={`/${Routes.TenantSelection}`} replace />;
    }

    // API requests use the selected tenant's ID in their headers.
    if (selectedTenantId !== tenant.id) {
        return <Initialize />;
    }

    return <TenantManagementContent key={tenant.id} tenant={tenant} />;
};

/**
 * Tenant-scoped application shell that keeps navigation fixed while page content scrolls.
 */
const TenantManagementContent = ({ tenant }: { tenant: Tenant }) => {
    const { t } = useTranslation();
    const title = useDocumentTitle();
    const [cmdOpen, setCmdOpen] = useState(false);
    const { data: userAccess } = useSuspenseUserAccess(tenant.id);

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen((v) => !v);
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    // Ensure userData is available (will be handled by suspense if not)
    if (!userAccess) {
        throw new Error("User access not available");
    }
    if (userAccess.length === 0) {
        return <NoRoleAssigned />;
    }

    return (
        <SidebarProvider className="h-svh">
            <AppSidebar tenant={tenant} />
            <SidebarInset>
                <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border/40 px-3">
                    <SidebarTrigger className="-ml-1" />
                    <Separator orientation="vertical" className="mr-1 h-4" />
                    <Breadcrumb>
                        <BreadcrumbList>
                            <BreadcrumbItem className="hidden md:block">
                                <BreadcrumbPage>{title}</BreadcrumbPage>
                            </BreadcrumbItem>
                        </BreadcrumbList>
                    </Breadcrumb>

                    <div className="ml-auto flex items-center gap-2">
                        <button
                            onClick={() => setCmdOpen(true)}
                            className="hidden h-8 min-w-[220px] items-center gap-2 rounded-md border bg-background px-2.5 text-xs text-muted-foreground transition-colors hover:border-foreground/20 hover:text-foreground sm:flex"
                        >
                            <Search className="size-3" />
                            {t("command.trigger")}
                            <kbd className="ml-auto rounded border bg-muted px-1.5 py-0.5 font-mono text-[10.5px] text-muted-foreground">
                                ⌘K
                            </kbd>
                        </button>

                        <ThemeToggle />
                        <LiveIndicator />
                    </div>
                </header>
                <div className="flex h-0 min-h-0 max-w-full flex-1 flex-col gap-6 overflow-y-auto overflow-x-hidden overscroll-contain p-6">
                    <div className="flex-1 min-h-0">
                        <Outlet />
                    </div>
                </div>
            </SidebarInset>
            <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />
            <Toaster />
        </SidebarProvider>
    );
};
